import React, { useMemo } from 'react';
import DataTableGrid from '../../../components/common/DataTableGrid';
import ExportTable from '../../../components/common/export-table/ExportTable';
import '../ESPDashboard.scss';

export interface ESPTableWidgetProps {
  widgetHeader: string;
  subHeader?: string;
  columnData: any[];
  tableData: any[];
}

const ESPTableWidget: React.FC<ESPTableWidgetProps> = ({ widgetHeader, columnData, tableData }) => {
  const columnsNoFilter = useMemo(
    () =>
      (columnData || []).map((col: any) =>
        col?.columns && Array.isArray(col?.columns)
          ? {
              ...col,
              disableFilters: true,
              columns: col.columns.map((obj: any) => ({ ...obj, disableFilters: true })),
            }
          : { ...col, disableFilters: true },
      ),
    [columnData],
  );

  const tableRowsForExport = useMemo(() => {
    return (tableData || []).map((item: any) => ({ original: item }));
  }, [tableData]);

  const rPermission = { canExport: true };

  const extractCellValue = (col: any, row: any, accessor: string): string => {
    if (!col.Cell || typeof col.Cell !== 'function') {
      const v = row.original?.[accessor];
      return v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    }

    try {
      const cellResult = col.Cell({ value: row.original?.[accessor], row, column: col });
      const children = cellResult?.props?.children;

      if (typeof children === 'string') return children;
      if (children?.props?.children) return children.props.children;

      const v = row.original?.[accessor];
      return v == null ? '' : String(v);
    } catch {
      const v = row.original?.[accessor];
      return v == null ? '' : String(v);
    }
  };

  // Export columns generator (excludes action columns)
  const exportColumns = useMemo(() => {
    const flatten = (cols: any[], groupHeader = ''): any[] => {
      return cols.reduce((out: any[], col: any) => {
        if (col.id === 'actions' || col.id === 'delete-actions') return out;

        if (Array.isArray(col.columns)) {
          const gh = typeof col.Header === 'string' ? col.Header : groupHeader;
          out.push(...flatten(col.columns, gh));
        } else {
          const header = typeof col.Header === 'string' ? col.Header : col.Header?.props?.children || '';
          const accessor = col.accessor || col.id || header;

          out.push({
            key: accessor,
            header,
            groupHeader,
            accessor: (row: any) => extractCellValue(col, row, accessor),
          });
        }
        return out;
      }, []);
    };

    return flatten(columnData || []);
  }, [columnData]);

  return (
    <div className='esp-dashboard-table-container'>
      <h1 className='esp-dashboard-table-header'>{widgetHeader}</h1>
      <div className='esp-export-btn'>
        <ExportTable
          data={tableRowsForExport}
          fileName={`${widgetHeader}`}
          sheetName={widgetHeader}
          columns={exportColumns}
          disabled={!tableRowsForExport || tableRowsForExport.length === 0}
          rPermission={rPermission}
        />
      </div>

      <div className='esp-dashboard-table-content'>
        <DataTableGrid data={tableData} columns={columnsNoFilter} maxHeight='100%' noRecordsHeight='130px' />
      </div>
    </div>
  );
};

export default ESPTableWidget;
